import React, { useState, useEffect } from "react";
import { doc, setDoc, updateDoc, getDoc } from "firebase/firestore";
import { Tooltip, Input, Spin } from "antd";
import CreateColorModal from "./CreateColorModal";
import "./StoreSettings.css";
import { db } from "../../../../services/firebaseService/connection";
import { invalidateStadiumCache, postUpdateCDNUtil } from "../../../../helperFunctions/util";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../store/adminData/adminData";
import logger from "../../../../utils/logger";
import ScreenHeader from "../../../../CommonComponents/ScreenHeader";
import DescriptionText from "../../../../CommonComponents/DescriptionText";
import ChantButton from "../../../../CommonComponents/ChantButton/ChantButton";
import {
  ChantCheckbox,
  ChantInput,
  ChantSelect,
} from "../../../../CommonComponents";
import { toast } from "react-toastify";

const shippingOptions = [
  { value: null, label: "Select option" },
  { value: "pickup", label: "Pick up only" },
  { value: "shipping", label: "Shipping only" },
  { value: "both", label: "Option of shipping or pickup" },
];

const countries = [
  "Australia",
  "Austria",
  "Belgium",
  "Brazil",
  "Canada",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hong Kong",
  "Hungary",
  "India",
  "Indonesia",
  "Ireland",
  "Italy",
  "Japan",
  "Malaysia",
  "Mexico",
  "Netherlands",
  "New Zealand",
  "Norway",
  "Poland",
  "Portugal",
  "Spain",
  "Sweden",
  "Switzerland",
  "Thailand",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
];

const processingFeeTypes = [
  { value: null, label: "Select option" },
  {
    value: "Group pays (Fees absorbed by group)",
    label: "Group pays (Fees absorbed by group)",
  },
  {
    value: "Member pays (Fees added to total)",
    label: "Member pays (Fees added to total)",
  },
  {
    value: "Ask Member to pay (Request member pay fee but not required)",
    label: "Ask Member to pay (Request member pay fee but not required)",
  },
];

const navigationOptions = [
  { value: null, label: "Select option" },
  { value: "Store", label: "Store" },
  { value: "Merch", label: "Merch" },
  { value: "Merchandise", label: "Merchandise" },
];

const StoreSettings = () => {
  const [state, setState] = useState({
    deliveryOption: "",
    shippingNote: "",
    pickupNote: "",
    publishStandalone: false,
    storeFeeOption: "",
    storeIntroText: "",
    shippingCountries: [],
    loading: false,
    showModal: false,
    navigationLabel: "",
    storeUrl: "",
  });
  const [fetchingData, setFetchingData] = useState(false);
  const [colors, setColors] = useState([]);

  const groupData = useAdminDataStore(selectCurrentGroupData);

  const changeSelect = (name, value) => {
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleInputChange = (name, value) => {
    setState((prevState) => ({ ...prevState, [name]: value }));
  };

  const fetchData = async () => {
    try {
      setFetchingData(true);
      const storeDoc = await getDoc(doc(db, "store", groupData.id));
      if (storeDoc.exists()) {
        const data = storeDoc.data();

        const publishStandalone = data.publishStandalone || false;
        setState({
          deliveryOption: data.deliveryOption || "",
          shippingNote: data.shippingNote || "",
          pickupNote: data.pickupNote || "",
          publishStandalone,
          storeFeeOption: data.storeFeeOption || "",
          processingFee: data.processingFee || "",
          storeIntroText: data.storeIntroText || "",
          shippingCountries: data.shippingCountries || [],
          navigationLabel:
            data.navigationLabel || (publishStandalone ? "Store" : ""),
          storeUrl: data.storeUrl || "",
          // storeUrl:
          //   data.link ||
          //   (publishStandalone
          //     ? `https://group.chant.fan/${groupData.groupName
          //         .toLowerCase()
          //         .replace(/ /g, "")}/store`
          //     : ""),
          loading: false,
          showModal: false,
        });
      }
    } catch (error) {
      logger.error(error);
      toast.error("Something went wrong");
    } finally {
      setFetchingData(false);
    }
  };

  const handleSubmit = async () => {
    if (!state.deliveryOption) {
      toast.error("Delivery option is required");
      return;
    }
    if (!state.shippingCountries || state.shippingCountries.length === 0) {
      toast.error("At least one shipping country is required");
      return;
    }
    if (!state.processingFee) {
      toast.error("Processing fee is required");
      return;
    }

    setState((prevState) => ({ ...prevState, loading: true }));
    const updateObj = { ...state };
    let { storeUrl, navigationLabel } = updateObj;

    if (storeUrl && !storeUrl.startsWith("https://")) {
      storeUrl = `https://${storeUrl}`;
    }

    const store = { name: navigationLabel, link: storeUrl };

    delete updateObj.loading;
    delete updateObj.showModal;

    updateObj.storeUrl = storeUrl;

    try {
      await setDoc(doc(db, "store", groupData?.id), updateObj, { merge: true });
      await updateDoc(doc(db, "group", groupData?.id), { store });
      postUpdateCDNUtil(groupData?.id);
      invalidateStadiumCache(groupData?.groupKey);
      toast.success("Store settings saved successfully");
      await fetchData();
    } catch (error) {
      logger.error(error);
      toast.error("Something went wrong");
    } finally {
      setState((prevState) => ({ ...prevState, loading: false }));
    }
  };

  useEffect(() => {
    if (groupData?.id) {
      fetchData();
      fetchColors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  const fetchColors = async () => {
    try {
      const docSnap = await getDoc(doc(db, "store", groupData.id));
      if (docSnap.exists()) {
        const data = docSnap.data();
        setColors(data.colors || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  return (
    <div className="store-settings-container" style={{ padding: "12px" }}>
      <ScreenHeader title="Settings" />

      {state.showModal && (
        <CreateColorModal
          visible={state.showModal}
          hideModal={() =>
            setState((prevState) => ({ ...prevState, showModal: false }))
          }
          currentGroup={groupData?.id}
          colors={colors}
          setColors={setColors}
        />
      )}

      <DescriptionText showBackground>
        Complete the required fields in settings before publishing items in the
        store. If items will be offered in varying colors establish a master
        color list here first.
      </DescriptionText>

      <div
        className="mx-auto"
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#ffffff",
          marginTop: "2px",

          // boxShadow: "0px 3px 10px 1px",
        }}
      >
        {fetchingData ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "300px" }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div className="my-row p-3">
              <div className="col-md-8">
                <div style={{ fontSize: 20, fontWeight: "bold" }}>
                  Delivery Settings
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div className="store-form-field-container">
                    <ChantSelect
                      label={
                        <span>
                          Delivery Options
                          <span style={{ color: "#ff0000" }}>*</span>
                        </span>
                      }
                      placeholder="Select option"
                      options={shippingOptions}
                      value={state.deliveryOption}
                      onChange={(value) => {
                        changeSelect("deliveryOption", value);
                      }}
                      dataKey="value"
                      dataValue="label"
                    />
                  </div>

                  <div className="store-form-field-container">
                    <ChantInput
                      label="Shipping note"
                      name="shippingNote"
                      value={state.shippingNote}
                      onChange={(e) =>
                        handleInputChange("shippingNote", e.target.value)
                      }
                    />
                  </div>
                  <div className="store-form-field-container">
                    <ChantInput
                      label="Pickup note"
                      name="pickupNote"
                      value={state.pickupNote}
                      onChange={(e) =>
                        handleInputChange("pickupNote", e.target.value)
                      }
                    />
                  </div>
                  <div className="store-form-field-container">
                    <div>
                      <span
                        style={{ paddingBottom: "6px", fontWeight: "bold" }}
                      >
                        Country Shipping Options
                        <span style={{ color: "#ff0000" }}>*</span>
                      </span>
                      <Tooltip
                        placement="topLeft"
                        title={
                          <div style={{ width: 400 }}>
                            <span>
                              Select countries where you offer shipping. Note:
                              Shipping is set as flat rate by product.
                            </span>
                          </div>
                        }
                      >
                        <span className="border-0">
                          &nbsp;<i className="fa fa-question-circle-o"></i>
                        </span>
                      </Tooltip>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "4px",
                      }}
                    >
                      {countries.map((country) => {
                        return (
                          <div
                            key={country}
                            className="my-row align-items-center"
                          >
                            <ChantCheckbox
                              label={country}
                              onChange={(e) => {
                                let shippingCountries =
                                  state.shippingCountries || [];

                                if (e.target.checked) {
                                  shippingCountries.push(country);
                                } else {
                                  shippingCountries.splice(
                                    shippingCountries.indexOf(country),
                                    1
                                  );
                                }

                                setState((prevState) => ({
                                  ...prevState,
                                  shippingCountries: shippingCountries,
                                }));
                              }}
                              checked={
                                state.shippingCountries &&
                                state.shippingCountries.includes(country)
                              }
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div
                    className="store-form-field-container"
                    style={{ margin: "12px 0" }}
                  >
                    <div>
                      <span style={{ fontWeight: "bold" }}>
                        Create Product Colors
                      </span>
                      <Tooltip
                        placement="topLeft"
                        title={
                          <div style={{ width: 400 }}>
                            <span>
                              Create a master list of color values here then
                              select colors when adding products.
                            </span>
                          </div>
                        }
                      >
                        <span className="border-0">
                          &nbsp;<i className="fa fa-question-circle-o"></i>
                        </span>
                      </Tooltip>
                    </div>
                    <div>
                      <span
                        style={{
                          textDecoration: "underline",
                          color: "blue",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setState((prevState) => ({
                            ...prevState,
                            showModal: true,
                          }));
                        }}
                      >
                        Set Colors
                      </span>
                      <span
                        style={{ marginLeft: "10px" }}
                      >{`(${colors?.join(", ")})`}</span>
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    fontSize: 20,
                    fontWeight: "bold",
                    marginTop: "20px",
                    marginBottom: "10px",
                  }}
                >
                  Store (Web)
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div
                    className="store-form-field-container"
                    style={{
                      display: "flex",
                      gap: "24px",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <ChantCheckbox
                        label={
                          <span>
                            {" "}
                            Publish standalone store webpage?{" "}
                            <Tooltip
                              placement="topLeft"
                              title={
                                <div style={{ width: 400 }}>
                                  <span>
                                    Select this option to generate a standalone
                                    store web page allowing you to link from
                                    your own website. If using a Chant website
                                    this option is not needed.
                                  </span>
                                </div>
                              }
                            >
                              <span className="border-0">
                                &nbsp;
                                <i className="fa fa-question-circle-o"></i>
                              </span>
                            </Tooltip>
                          </span>
                        }
                        checked={state.publishStandalone}
                        onChange={(e) => {
                          let publish = e.target.checked;
                          let updatedState = {
                            publishStandalone: publish,
                          };
                          if (publish) {
                            updatedState = {
                              ...updatedState,
                              storeUrl:
                                "https://group.chant.fan/" +
                                groupData?.groupName
                                  .replace(/ /g, "")
                                  .toLowerCase() +
                                "/store",
                              navigationLabel: "Store",
                            };
                          } else {
                            if (state.storeUrl.includes("group.chant.fan")) {
                              updatedState = {
                                ...updatedState,
                                storeUrl: "",
                                navigationLabel: "",
                              };
                            }
                          }
                          setState((prev) => ({
                            ...prev,
                            publishStandalone: updatedState?.publishStandalone,
                            storeUrl: updatedState?.storeUrl,
                            navigationLabel: updatedState?.navigationLabel,
                          }));
                        }}
                        color="primary"
                        tabIndex="5"
                        id="publishStandalone"
                      />
                    </div>
                    <div>
                      Status:{" "}
                      {state.publishStandalone ? (
                        <span>
                          Published (
                          <a
                            href={`https://group.chant.fan/${groupData?.groupName.toLowerCase().replace(/ /g, "")}/store`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Store
                          </a>
                          )
                        </span>
                      ) : (
                        "Unpublished"
                      )}
                    </div>
                  </div>

                  <div className="store-form-field-container">
                    <div>
                      <span style={{ fontWeight: "bold" }}>
                        Store Intro Text
                      </span>
                    </div>
                    <div>
                      <Input.TextArea
                        onChange={(e) => {
                          handleInputChange("storeIntroText", e.target.value);
                        }}
                        name="storeIntroText"
                        value={state.storeIntroText}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: "bold",
                    marginTop: "20px",
                  }}
                >
                  Store navigation(App)
                </div>
                <div style={{ marginBottom: "20px" }}>
                  <p>
                    Using the Chant Store or an external store? Add navigation
                    name + store url to appear in More section of the app as web
                    link. Note that we will integrate the Store into the app in
                    the coming months – currently web only.
                  </p>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  <div className="store-form-field-container">
                    <ChantSelect
                      label="Navigation name"
                      value={state.navigationLabel}
                      onChange={(value) =>
                        changeSelect("navigationLabel", value)
                      }
                      name="navigationLabel"
                      dataKey="value"
                      dataValue="label"
                      options={navigationOptions}
                    />
                  </div>

                  <div className="store-form-field-container">
                    <ChantInput
                      label="Store Url"
                      value={state.storeUrl}
                      onChange={(e) =>
                        handleInputChange("storeUrl", e.target.value)
                      }
                      name="storeUrl"
                      placeholder="Store URL"
                    />
                  </div>

                  <div className="store-form-field-container">
                    <ChantSelect
                      label={
                        <span>
                          Processing Fee
                          <span style={{ color: "#ff0000" }}>*</span>
                          <Tooltip
                            placement="topLeft"
                            title={
                              <div>
                                <p style={{ fontWeight: "bold" }}>Fees</p>
                                <p style={{ marginBottom: 0 }}>
                                  Stripe card processing fee: 2.9% + $0.30 (can
                                  vary)
                                </p>
                                <p>Chant fee: 3%</p>

                                <p style={{ fontWeight: "bold" }}>Member Pay</p>
                                <p>
                                  Stripe collects the fee on the entire purchase
                                  amount. So, if a member covers fees that is
                                  added to the total charged ($10 item + $0.59
                                  fee paid by member for $10.59) stripe will
                                  collect a fee on the entire $10.59.
                                </p>

                                <p>
                                  To account for this we add 3% + $.30 (.1%
                                  more) to the original item price for Stripe so
                                  your net is close to the item price - it may
                                  be off very slightly.
                                </p>

                                <p>
                                  Stripe fees can vary based on member payment
                                  method but are not known until after the
                                  transaction is complete (foreign card, etc).
                                  Your net proceeds may not cover the full item
                                  amount but should be close.
                                </p>

                                <p style={{ fontWeight: "bold" }}>
                                  Member Pay (legal Limitations)
                                </p>
                                <p>
                                  Not all states/countries allow the fee to be
                                  passed to the member. Chant is not responsible
                                  for determining your local laws. Please
                                  confirm this for your location.
                                </p>
                              </div>
                            }
                          >
                            <span className="border-0">
                              &nbsp;<i className="fa fa-question-circle-o"></i>
                            </span>
                          </Tooltip>
                        </span>
                      }
                      name="processingFee"
                      value={state.processingFee}
                      onChange={(value) => changeSelect("processingFee", value)}
                      dataKey="value"
                      dataValue="label"
                      options={processingFeeTypes}
                    />
                    <span style={{ fontSize: "12px", color: "#000000aa" }}>
                      Stripe (2.9% + $.030) + Chant (3%). See &apos;?&apos; for
                      additional details.
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="my-row my-4 justify-content-center">
              <ChantButton
                onClick={handleSubmit}
                loading={state.loading}
                disabled={state.loading}
              >
                Submit
              </ChantButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StoreSettings;
